import { useState, useEffect, useRef } from "react";
import { Star, MessageSquare, Mail, Send, ThumbsUp, ThumbsDown, Plus, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface ProjectFeedbackTabProps {
  project: any;
  isEditingInline: boolean;
  onEditingChange: (editing: boolean) => void;
}

const StarRating = ({ 
  rating, 
  onRatingChange,
  readonly = false 
}: { 
  rating: number; 
  onRatingChange?: (rating: number) => void;
  readonly?: boolean;
}) => {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-5 w-5 cursor-pointer transition-colors ${
            star <= rating
              ? "text-yellow-400 fill-yellow-400"
              : "text-gray-300 hover:text-yellow-300"
          } ${readonly ? "cursor-default" : ""}`}
          onClick={() => !readonly && onRatingChange?.(star)}
        />
      ))}
      <span className="ml-2 text-sm text-muted-foreground">
        {rating > 0 ? `${rating}/5` : "Not rated"}
      </span>
    </div>
  );
};

export function ProjectFeedbackTab({ project, isEditingInline, onEditingChange }: ProjectFeedbackTabProps) {
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch internal comments with manual join
  const { data: internalComments = [] } = useQuery({
    queryKey: ["project-internal-comments", project.id],
    queryFn: async () => {
      // First get the comments
      const { data: comments, error: commentsError } = await supabase
        .from("project_internal_comments")
        .select("*")
        .eq("project_id", project.id)
        .order("created_at", { ascending: false });

      if (commentsError) throw commentsError;
      if (!comments || comments.length === 0) return [];

      // Then get the user profiles for these comments
      const userIds = comments.map(comment => comment.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name, profile_image_url")
        .in("user_id", userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      return comments.map(comment => ({
        ...comment,
        profiles: profiles?.find(p => p.user_id === comment.user_id) || null
      }));
    },
  });

  // Fetch people associated with the client
  const { data: clientPeople = [] } = useQuery({
    queryKey: ["client-people", project.client_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_people_secure", {
          client_filter: project.client_id,
          status_filter: "active"
        });

      if (error) throw error;
      return data || [];
    },
    enabled: !!project.client_id,
  });

  // Fetch project feedback responses
  const { data: feedbackResponses = [] } = useQuery({
    queryKey: ["project-feedback-responses", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_feedback_responses")
        .select("*")
        .eq("project_id", project.id)
        .order("submitted_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Initialize selected emails when client people are loaded
  useEffect(() => {
    if (clientPeople && clientPeople.length > 0) {
      const emails = clientPeople
        .filter(person => person.email && person.email.trim() !== "")
        .map(person => person.email);
      setSelectedEmails(emails);
    }
  }, [clientPeople]);

  // Auto-focus email input when dialog opens
  useEffect(() => {
    if (isDialogOpen) {
      // Small delay to ensure the dialog is fully rendered
      const timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isDialogOpen]);

  const handleEmailToggle = (email: string, checked: boolean) => {
    if (checked) {
      setSelectedEmails(prev => [...prev, email]);
    } else {
      setSelectedEmails(prev => prev.filter(e => e !== email));
    }
  };

  const handleAddEmail = () => {
    if (newEmailInput.trim() && !selectedEmails.includes(newEmailInput.trim()) && !additionalEmails.includes(newEmailInput.trim())) {
      setAdditionalEmails(prev => [...prev, newEmailInput.trim()]);
      setSelectedEmails(prev => [...prev, newEmailInput.trim()]);
      setNewEmailInput("");
    }
  };

  const handleRemoveAdditionalEmail = (email: string) => {
    setAdditionalEmails(prev => prev.filter(e => e !== email));
    setSelectedEmails(prev => prev.filter(e => e !== email));
  };

  const allSelectedEmails = [...selectedEmails];
  const clientEmails = clientPeople.filter(p => p.email).map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }));

  const addCommentMutation = useMutation({
    mutationFn: async (commentText: string) => {
      const { data, error } = await supabase
        .from("project_internal_comments")
        .insert({
          project_id: project.id,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          comment: commentText,
          org_id: project.org_id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-internal-comments", project.id] });
      setNewComment("");
      toast({
        title: "Success",
        description: "Comment added successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to add comment: " + error.message,
        variant: "destructive",
      });
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, commentText }: { commentId: string; commentText: string }) => {
      const { data, error } = await supabase
        .from("project_internal_comments")
        .update({ comment: commentText })
        .eq("id", commentId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-internal-comments", project.id] });
      setEditingCommentId(null);
      setEditingCommentText("");
      toast({
        title: "Success",
        description: "Comment updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error", 
        description: "Failed to update comment: " + error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from("project_internal_comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-internal-comments", project.id] });
      toast({
        title: "Success",
        description: "Comment deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to delete comment: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleAddComment = () => {
    if (newComment.trim()) {
      addCommentMutation.mutate(newComment.trim());
    }
  };

  const handleEditComment = (commentId: string, currentText: string) => {
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  };

  const handleSaveEdit = () => {
    if (editingCommentId && editingCommentText.trim()) {
      updateCommentMutation.mutate({
        commentId: editingCommentId,
        commentText: editingCommentText.trim(),
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const handleDeleteComment = (commentId: string) => {
    deleteCommentMutation.mutate(commentId);
  };

  const sendFeedbackRequestMutation = useMutation({
    mutationFn: async (emails: string[]) => {
      // Get client name from the project data
      const clientName = project.clients?.name || "Unknown Client";
      
      const { data, error } = await supabase.functions.invoke('send-feedback-request', {
        body: {
          project_name: project.name,
          project_id: project.id,
          client_name: clientName,
          emails: emails
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "NPS Request Sent",
        description: data.message || `Email has been sent to ${allSelectedEmails.length} recipient(s)`,
      });
      setIsDialogOpen(false); // Close the dialog
      // Reset email selections for next use
      setNewEmailInput("");
      setAdditionalEmails([]);
      // Re-initialize selected emails from client people
      const emails = clientPeople
        .filter(person => person.email && person.email.trim() !== "")
        .map(person => person.email);
      setSelectedEmails(emails);
      // Refresh feedback responses to show any new ones
      queryClient.invalidateQueries({ queryKey: ["project-feedback-responses", project.id] });
    },
    onError: (error: any) => {
      console.error("Feedback request error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send NPS request",
        variant: "destructive",
      });
    },
  });

  const deleteFeedbackResponseMutation = useMutation({
    mutationFn: async (responseId: string) => {
      const { error } = await supabase
        .from("project_feedback_responses")
        .delete()
        .eq("id", responseId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Feedback response deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["project-feedback-responses", project.id] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to delete feedback response: " + error.message,
        variant: "destructive",
      });
    },
  });

  const handleSendFeedbackRequest = () => {
    if (allSelectedEmails.length === 0) {
      toast({
        title: "No Recipients",
        description: "Please select at least one email address.",
        variant: "destructive",
      });
      return;
    }
    sendFeedbackRequestMutation.mutate(allSelectedEmails);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Internal Feedback */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2" />
              Our comments
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add new comment */}
            <div className="space-y-2">
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add an internal comment..."
                rows={3}
              />
              <Button 
                onClick={handleAddComment}
                disabled={!newComment.trim() || addCommentMutation.isPending}
                size="sm"
              >
                {addCommentMutation.isPending ? "Adding..." : "Add Comment"}
              </Button>
            </div>

            <Separator />

            {/* Comments list */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {internalComments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No internal comments yet
                </p>
              ) : (
                internalComments.map((comment: any) => (
                  <div key={comment.id} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center space-x-3">
                        {/* User photo */}
                        {comment.profiles?.profile_image_url ? (
                          <img 
                            src={comment.profiles.profile_image_url} 
                            alt={`${comment.profiles.first_name} ${comment.profiles.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-medium">
                            {comment.profiles?.first_name?.charAt(0) || 'U'}
                          </div>
                        )}
                        
                        {/* Date without time */}
                        <p className="text-xs text-muted-foreground">
                          {new Date(comment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      {/* X button on far right */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteComment(comment.id)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {editingCommentId === comment.id ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editingCommentText}
                          onChange={(e) => setEditingCommentText(e.target.value)}
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            onClick={handleSaveEdit}
                            disabled={!editingCommentText.trim() || updateCommentMutation.isPending}
                          >
                            {updateCommentMutation.isPending ? "Saving..." : "Save"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">
                        {comment.comment}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Client NPS */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Star className="h-5 w-5 mr-2" />
              Client NPS
            </CardTitle>
            {feedbackResponses.length > 0 && (
              <div className="flex items-center justify-center mt-4">
                <div className="bg-secondary/20 px-6 py-3 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">
                      {(feedbackResponses.reduce((sum: number, response: any) => sum + response.nps_score, 0) / feedbackResponses.length).toFixed(1)}
                    </div>
                    <div className="text-sm text-muted-foreground">Average NPS</div>
                  </div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <Separator />

              <div className="space-y-3">

              <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                    <Mail className="h-4 w-4 mr-2" />
                    Send NPS request
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-md">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send NPS Request</AlertDialogTitle>
                    <AlertDialogDescription>
                      Select the email addresses that will receive the NPS request for "{project.name}".
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  
                  <div className="space-y-4 max-h-80 overflow-y-auto">
                    {/* No contacts message */}
                    {clientEmails.length === 0 && additionalEmails.length === 0 && (
                      <div className="text-center py-4 text-muted-foreground">
                        <p>No contacts found for this client.</p>
                        <p className="text-sm">Add email addresses below to send feedback requests.</p>
                      </div>
                    )}

                    {/* Client emails */}
                    {clientEmails.length > 0 && (
                      <div>
                        <Label className="text-sm font-medium">Client Contacts:</Label>
                        <div className="space-y-2 mt-2">
                          {clientEmails.map((contact) => (
                            <div key={contact.email} className="flex items-center space-x-2">
                              <Checkbox 
                                id={contact.email}
                                checked={selectedEmails.includes(contact.email)}
                                onCheckedChange={(checked) => handleEmailToggle(contact.email, checked as boolean)}
                              />
                              <Label htmlFor={contact.email} className="flex-1 text-sm cursor-pointer">
                                {contact.name} ({contact.email})
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Additional emails */}
                    {additionalEmails.length > 0 && (
                      <div>
                        <Label className="text-sm font-medium">Additional Emails:</Label>
                        <div className="space-y-2 mt-2">
                          {additionalEmails.map((email) => (
                            <div key={email} className="flex items-center space-x-2">
                              <Checkbox 
                                id={`additional-${email}`}
                                checked={selectedEmails.includes(email)}
                                onCheckedChange={(checked) => handleEmailToggle(email, checked as boolean)}
                              />
                              <Label htmlFor={`additional-${email}`} className="flex-1 text-sm cursor-pointer">
                                {email}
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveAdditionalEmail(email)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add new email */}
                    <div>
                      <Label className="text-sm font-medium">Add Email:</Label>
                      <div className="flex space-x-2 mt-2">
                        <Input
                          ref={emailInputRef}
                          type="email"
                          placeholder="email@example.com"
                          value={newEmailInput}
                          onChange={(e) => setNewEmailInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddEmail();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleAddEmail}
                          disabled={!newEmailInput.trim()}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Selected emails summary */}
                    {allSelectedEmails.length > 0 && (
                      <div className="pt-2 border-t">
                        <Label className="text-sm font-medium">
                          Will send to {allSelectedEmails.length} recipient(s):
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          {allSelectedEmails.join(", ")}
                        </p>
                      </div>
                    )}
                  </div>

                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleSendFeedbackRequest}
                      disabled={allSelectedEmails.length === 0 || sendFeedbackRequestMutation.isPending}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sendFeedbackRequestMutation.isPending ? "Sending..." : `Send Request (${allSelectedEmails.length})`}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              </div>

              {/* Display feedback responses */}
              {feedbackResponses.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Received NPS:</Label>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {feedbackResponses.map((response: any) => (
                      <div key={response.id} className="border rounded-lg p-3 bg-muted/50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium text-sm">
                              {response.recipient_name || response.email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(response.submitted_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="flex items-center space-x-1">
                              <span className="text-sm font-medium">NPS:</span>
                              <Badge variant={
                                response.nps_score >= 9 ? "nps-high" : 
                                response.nps_score >= 7 ? "nps-medium" : 
                                "nps-low"
                              }>
                                {response.nps_score}/10
                              </Badge>
                            </div>
                            {isEditingInline && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteFeedbackResponseMutation.mutate(response.id)}
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {response.comments && (
                          <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">
                            {response.comments}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
      </div>
    </div>
  );
}